import React, {
  FC,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from 'react'
import { cx } from '@emotion/css'

import styles from './autocompleteDropdown.styles'
import { escapeStringRegexp } from '../../../utils/escapeStringRegexp'
import { Box, ChakraProps } from '@chakra-ui/react'
import { minMax } from 'front-end/src/utils/minMax'
import { plainSearch } from 'front-end/src/components/common/selectEntitiesForm/utils'
import { MatchedSearchIndexes } from 'front-end/src/components/common/selectEntitiesForm'
import { HighlightMatchedString } from 'front-end/src/components/common/selectEntitiesForm/HighlightMatchedString'

interface AutocompleteDropdownProps extends ChakraProps {
  options: string[]
  value?: string
  setValue?: (value: string) => void
  setOpen: (isOpened: boolean) => void
  isOpened: boolean
  enableArrowSelection?: boolean
  withHighlightSearch?: boolean
}

const DEFAULT_ITEM_HEIGHT = 40

const ARROW_DOWN = 'ArrowDown'
const ARROW_UP = 'ArrowUp'
const ARROW_KEYS = [ARROW_DOWN, ARROW_UP]

export const AutocompleteDropdown: FC<AutocompleteDropdownProps> = ({
  options,
  value,
  setValue,
  isOpened,
  setOpen,
  enableArrowSelection = false,
  withHighlightSearch = false,
  ...props
}) => {
  const [highlightedOption, setHighlightedOption] = useState<
    string | undefined
  >()
  const hasInitHighlighted = useRef(false)
  const scrollbarRef = useRef<HTMLDivElement>(null)

  const scrollToActive = (activeValue?: string | null) => {
    if (!activeValue) return

    const currentIndex = options.indexOf(activeValue)
    const scrollbarsRef = scrollbarRef.current

    if (!scrollbarsRef || currentIndex === -1) return

    const { height: wrapperHeight } = scrollbarsRef.getBoundingClientRect()
    const elementTop = currentIndex * DEFAULT_ITEM_HEIGHT
    const elementBottom = elementTop + DEFAULT_ITEM_HEIGHT
    const currentViewTop = scrollbarsRef.scrollTop
    const currentViewBottom = currentViewTop + wrapperHeight

    if (elementBottom <= currentViewBottom && elementTop >= currentViewTop) {
      return
    }

    scrollbarsRef.scrollTop = elementTop
  }

  const findAndHighlightOption = useCallback(() => {
    const searchRegexp = new RegExp(escapeStringRegexp(value || ''), 'i')
    const foundOption = options.find(
      (option) => value && option.match(searchRegexp)
    )

    setHighlightedOption(foundOption)
  }, [options, value])

  useEffect(findAndHighlightOption, [findAndHighlightOption])

  useEffect(() => {
    if (isOpened && !hasInitHighlighted.current) {
      hasInitHighlighted.current = true
      findAndHighlightOption()
    }

    if (!isOpened) {
      hasInitHighlighted.current = false
      if (highlightedOption) {
        setHighlightedOption(undefined)
      }
    }
  }, [isOpened, highlightedOption, findAndHighlightOption])

  const isEmpty = options.length <= 0

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!enableArrowSelection) return

      const isArrowPressed = ARROW_KEYS.includes(event.key)

      if (isArrowPressed) {
        event.preventDefault()

        if (!highlightedOption) return

        const activeIndex = options.indexOf(highlightedOption)

        if (activeIndex === -1) {
          return
        }

        const step = event.key === ARROW_UP ? -1 : 1
        const nextIndex = minMax(activeIndex + step, 0, options.length - 1)

        setHighlightedOption(options[nextIndex])
      }
    }

    if (highlightedOption) {
      setTimeout(() => {
        scrollToActive(highlightedOption)
      }, 0)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [highlightedOption, options, enableArrowSelection])

  const itemsToHighlight = useMemo<{
    [key: string]: MatchedSearchIndexes[]
  }>(() => {
    if (!value || !options || !withHighlightSearch) return {}

    const foundList = plainSearch<string>(options, value.trim(), (v) =>
      v.toLowerCase().indexOf(value.toLowerCase())
    )

    return foundList.reduce((foundMap, item) => {
      foundMap[item.item] = item.matches?.map((m) => m.indices) ?? []
      return foundMap
    }, {} as { [key: string]: MatchedSearchIndexes[] })
  }, [options, value, withHighlightSearch])

  if (isEmpty || !isOpened) return null

  return (
    <Box {...props} className={styles}>
      <Box className="optionsWr" ref={scrollbarRef}>
        {options.map((option, index) => (
          <Box
            className={cx(
              'option',
              highlightedOption === option && 'optionActive'
            )}
            onClick={() => {
              if (setValue) setValue(option)
              setOpen(false)
            }}
            onMouseEnter={() => setHighlightedOption(option)}
            key={index}
            role="button"
            // needed to get active value outside component
            data-option={option}
          >
            {withHighlightSearch && itemsToHighlight[option] ? (
              <HighlightMatchedString
                matches={itemsToHighlight[option]}
                text={option}
              />
            ) : (
              option
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
